import React, { useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Searchable + scrollable combobox. options: [{ value, label }].
 * Type to filter (cmdk filters on the item label) or scroll the full list.
 */
export function Combobox({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No match found.",
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  // See MultiSelect: rendered inside a Dialog/Sheet so touch scrolling survives the scroll
  // lock, and bounded by that dialog so the list is never clipped by its edges.
  const host = open ? triggerRef.current?.closest('[role="dialog"]') || undefined : undefined;
  const selected = options.find((o) => o.value === value);
  // Reference lists can pin a value to the top (e.g. United States); it gets its own
  // block with a divider so the A–Z list underneath still reads normally.
  const pinned = options.filter((o) => o.pinned);
  const rest = pinned.length ? options.filter((o) => !o.pinned) : options;
  const item = (o) => (
    <CommandItem
      key={o.value}
      value={o.label}
      onSelect={() => { onChange(o.value); setOpen(false); }}
      className="py-2.5 sm:py-1.5"
      data-testid={testId ? `${testId}-option-${o.value}` : undefined}
    >
      <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
      {o.label}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground", className)}
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flex max-h-[min(60dvh,var(--radix-popover-content-available-height))] w-[--radix-popover-trigger-width] flex-col overflow-hidden p-0"
        align="start" collisionPadding={12} container={host} collisionBoundary={host}>
        <Command className="flex min-h-0 max-h-full flex-col">
          <CommandInput placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined} />
          {/* Sized to the space Radix actually has, so the first rows are never clipped when
              the list opens upwards; single scroll container for reliable touch scrolling. */}
          <CommandList className="min-h-0 max-h-none flex-1 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            {pinned.length > 0 && (
              <>
                <CommandGroup>{pinned.map(item)}</CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>{rest.map(item)}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
